import { storageDateFormat } from '@/core';
import { loadplanningOrderFilter } from '@/dataStructures';
import deepCopy from 'deepcopy';
import moment from 'moment';
import Decimal from 'decimal.js';
import searchFilterMutations from './searchFilter/OrderSearch.mutations.js';

let setValue = (state, { value, key }) => {
	state[key] = value;
};

function processSubOrders(subOrders) {
	let processedSubOrders = [];
	for (let subOrder of subOrders) {
		if (subOrder.freightLines) {
			subOrder.freightLines = Object.values(subOrder.freightLines);
			subOrder.numberOfPallets = subOrder.freightLines.reduce((p, c) => p.plus(c.numberOfPallets), new Decimal(0)).toNumber();
			subOrder.grossWeight = subOrder.freightLines.reduce((p, c) => p.plus(c.grossWeight), new Decimal(0)).toNumber();
			subOrder.volume = subOrder.freightLines.reduce((p, c) => p.plus(c.volume), new Decimal(0)).toNumber();
		}
		processedSubOrders.push(subOrder);
	}
	return processedSubOrders;
}

let clearOrders = (state) => {
	state.orders = [];
	state.preloadedOrderData = [];
	state.totals.orders = 0;
	state.totals.volume = 0;
	state.totals.weight = 0;
	state.totals.loadingMeters = 0;
	state.totals.totalPrice = 0;
};

let setTotals = (state, totals) => {
	state.totals.orders = totals.count;
	state.totals.volume = totals.volume;
	state.totals.weight = totals.grossWeight;
	state.totals.loadingMeters = totals.loadingMeters;
	state.totals.totalPrice = totals.totalPrice;
	state.totals.baseCurrency = totals.baseCurrency || 'NOK';
};

let setPolledOrders = (state, response) => {
	state.polledOrders = response;
};

let refreshMatchingSubOrders = function(state, { orders, ids }) {
	let lookup = new Set(ids);
	state.orders = state.orders.filter(x => !lookup.has(x.id));
	let refreshedOrders = processSubOrders(orders);
	state.orders = state.orders.concat(refreshedOrders);
};

let refreshSubOrdersFromOrderIds = function(state, { orders, originalOrderIds }) {
	let lookup = new Set(originalOrderIds);
	state.orders = state.orders.filter(x => !lookup.has(x.orderId));
	let refreshedOrders = processSubOrders(orders);
	state.orders = state.orders.concat(refreshedOrders);
};

let updateMatchingSubOrders = function(state, orders) {
	let refreshedOrders = processSubOrders(orders);
	if (state.orders.length === 0) state.orders = refreshedOrders;
	else {
		let lookup = {};
		for (let i = 0; i < state.orders.length; i++)
			lookup[state.orders[i].id] = i;
		for (let order of refreshedOrders) {
			let index = lookup[order.id];
			if (index === undefined)
				state.orders.push(order);
			else
				state.orders[index] = order;
		}
	}
};

let loadSavedSearch = (state, savedSearch) => {
	if (!savedSearch) return;

	state.loadedSearch = { ...savedSearch };

	if (savedSearch.id === -1) { // reset
		state.searchFilter = loadplanningOrderFilter();
		state.loadedSearch = { id:-1, labelKey: 'searchForOrders' };
	} else {
		if (savedSearch.timeInterval.before) {
			let date = moment().add(savedSearch.timeInterval.before,'d').startOf('day');
			state.searchFilter.date.from = date.format(storageDateFormat);
		}
		if (savedSearch.timeInterval.after) {
			let date = moment().add(savedSearch.timeInterval.after,'d').endOf('day');
			state.searchFilter.date.to = date.format(storageDateFormat);
		}
		state.searchFilter.dateType = deepCopy(savedSearch.dateType) || 'etd';
		state.searchFilter.searchAreas = deepCopy(savedSearch.searchAreas || []);
		state.searchFilter.fromDespatchParty = deepCopy(savedSearch.fromDespatchParty || []);
		state.searchFilter.toDeliveryParty = deepCopy(savedSearch.toDeliveryParty || []);
		state.searchFilter.customer = deepCopy(savedSearch.customer || []);
		state.searchFilter.goodsTypes = deepCopy(savedSearch.goodsTypes || []);
		state.searchFilter.orderTypes = deepCopy(savedSearch.orderTypes || []);
		state.searchFilter.economicalStatus = deepCopy(savedSearch.economicalStatus || []);
		state.searchFilter.orderStatus = deepCopy(savedSearch.orderStatus || []);
		state.searchFilter.references = deepCopy(savedSearch.references || []);
		state.searchFilter.referencesText = deepCopy(savedSearch.referencesText) || '';
		state.searchFilter.hasAtd = deepCopy(savedSearch.hasAtd || { value: undefined, label: 'showAny' });
		state.searchFilter.planned = deepCopy(savedSearch.planned || false);
		state.searchFilter.waybillNumber = deepCopy(savedSearch.waybillNumber || []);
		state.searchFilter.mustHaveInboundEdi = savedSearch.mustHaveInboundEdi;
		state.searchFilter.inboundEdi = savedSearch.inboundEdi || '';
		state.searchFilter.mustHaveDeviation = savedSearch.mustHaveDeviation;
	}
};

let setSelectedOriginalOrder = (state, order) => {
	if (!order) {
		state.selectedOriginalOrder = {};
		return;
	}
	state.selectedOriginalOrder = order;
};

let set = (state, fn) => {
	if (fn)
		fn(state);
};

export default {
	...searchFilterMutations,
	setValue,
	clearOrders,
	setTotals,
	setPolledOrders,
	refreshMatchingSubOrders,
	refreshSubOrdersFromOrderIds,
	updateMatchingSubOrders,
	loadSavedSearch,
	setSelectedOriginalOrder,
	set,
};
